import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import TokenIcon from '@material-design-icons/svg/filled/token.svg?react';
import type {
  Dapp,
  Publisher as PublisherDto,
  Release as ReleaseDto,
} from '@src/dto';
import type { LooseNftMetadata } from '../../types/api';
import { formatDate } from '../../utils/stringHelpers';
import { buildReleaseNftName } from '../../utils/releaseNftName';
import { getStatusDisplay } from '../../utils/releaseStatusConfig';
import { Button } from '../Button';
import { ReleaseNftMintModal } from '../ReleaseNftMintModal';
import { VerifyCollectionButton } from '../VerifyCollectionButton';
import { BuildSection } from './components/BuildSection';
import { NftInformationSection } from './components/NftInformationSection';
import { PermissionsSection } from './components/PermissionsSection';
import { ResubmitHubspotButton } from './ResubmitHubspotButton';

interface ReleaseLoadedContentProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  publisher: PublisherDto | null;
  nftMetadata: LooseNftMetadata | null;
  apkDownloadUrl: string | null;
  arweaveApkUrl: string | null;
  canManageReleaseArtifacts: boolean;
  canSubmitReleaseToStore: boolean;
  showMintModal: boolean;
  onBack: () => void;
  onReloadRelease: () => void | Promise<void>;
  onMintClick: () => void;
  onMintClose: () => void;
  onMintSuccess: (mintAddress: string, txSignature: string) => void;
  onVerificationSuccess: () => Promise<void>;
}

export function ReleaseLoadedContent({
  release,
  dapp,
  publisher,
  nftMetadata,
  apkDownloadUrl,
  arweaveApkUrl,
  canManageReleaseArtifacts,
  canSubmitReleaseToStore,
  showMintModal,
  onBack,
  onReloadRelease,
  onMintClick,
  onMintClose,
  onMintSuccess,
  onVerificationSuccess,
}: ReleaseLoadedContentProps) {
  return (
    <div className="flex flex-col h-full bg-[#0f1011]">
      <ReleaseHeader release={release} dapp={dapp} onBack={onBack} />

      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-5xl mx-auto">
          <ReleaseActions
            release={release}
            dapp={dapp}
            canManageReleaseArtifacts={canManageReleaseArtifacts}
            canSubmitReleaseToStore={canSubmitReleaseToStore}
            onReloadRelease={onReloadRelease}
            onMintClick={onMintClick}
            onVerificationSuccess={onVerificationSuccess}
          />

          <BuildSection
            release={release}
            apkDownloadUrl={apkDownloadUrl}
            arweaveApkUrl={arweaveApkUrl}
          />

          <NftInformationSection
            release={release}
            dapp={dapp}
            nftMetadata={nftMetadata}
          />

          <PermissionsSection permissions={release.permissions || []} />
        </div>
      </div>

      <ReleaseMintModalMount
        release={release}
        dapp={dapp}
        publisher={publisher}
        showMintModal={showMintModal}
        onMintClose={onMintClose}
        onMintSuccess={onMintSuccess}
      />
    </div>
  );
}

interface ReleaseHeaderProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  onBack: () => void;
}

function ReleaseHeader({ release, dapp, onBack }: ReleaseHeaderProps) {
  const statusDisplay = getStatusDisplay(
    release.status,
    release.submissionStatus,
    {
      isLive: release.isLive,
      liveVersionComparison: release.liveVersionComparison,
      hubspotTicketId: release.hubspotTicketId,
      hubspotTicketClosedDate: release.hubspotTicketClosedDate,
      nftMintAddress: release.nftMintAddress,
      isSupersededByNewerSubmission: release.isSupersededByNewerSubmission,
    },
  );
  const StatusIcon = statusDisplay.icon;
  const submittedDate = release.publishedAt
    ? new Date(release.publishedAt)
    : release.submittedAt
      ? new Date(release.submittedAt)
      : new Date(release.created);
  const heroTitle = `${dapp?.dappName ? `${dapp.dappName} ` : ''}${
    release.versionName || 'Unknown'
  }`;

  return (
    <header className="border-b border-white/5">
      <div className="px-8 py-6 max-w-5xl mx-auto w-full">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors mb-4 -ml-1"
        >
          <ArrowBackIcon className="size-4 fill-current" />
          Back to releases
        </button>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight truncate">
              {heroTitle}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500">
              <span>
                Submitted{' '}
                {formatDate(
                  submittedDate,
                  { day: 'numeric', month: 'short', year: 'numeric' },
                  { locale: 'en-GB' },
                )}
              </span>
              {release.versionCode != null && (
                <>
                  <span aria-hidden="true" className="text-neutral-700">
                    ·
                  </span>
                  <span>
                    v
                    <span className="tabular-nums text-neutral-400 ml-0.5">
                      {release.versionCode}
                    </span>
                  </span>
                </>
              )}
              {release.hubspotTicketId && (
                <>
                  <span aria-hidden="true" className="text-neutral-700">
                    ·
                  </span>
                  <span className="font-mono">#{release.hubspotTicketId}</span>
                </>
              )}
            </div>
          </div>

          <div className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0">
            <StatusIcon
              className={`shrink-0 pointer-events-none ${statusDisplay.iconClassName}`}
            />
            <span
              className={`text-sm font-medium ${statusDisplay.textColorClassName}`}
            >
              {statusDisplay.label}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}

interface ReleaseActionsProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  canManageReleaseArtifacts: boolean;
  canSubmitReleaseToStore: boolean;
  onReloadRelease: () => void | Promise<void>;
  onMintClick: () => void;
  onVerificationSuccess: () => Promise<void>;
}

function ReleaseActions({
  release,
  dapp,
  canManageReleaseArtifacts,
  canSubmitReleaseToStore,
  onReloadRelease,
  onMintClick,
  onVerificationSuccess,
}: ReleaseActionsProps) {
  return (
    <>
      {canSubmitReleaseToStore && (
        <ResubmitHubspotButton
          releaseId={release.id}
          dappName={dapp?.dappName || ''}
          whatsNew={release.newInVersion || ''}
          isProcessed={release.status === 'Processed'}
          hasNft={!!release.nftMintAddress}
          hubspotTicketId={release.hubspotTicketId}
          isCollectionVerified={release.isCollectionVerified}
          submissionStatus={release.submissionStatus}
          onSuccess={onReloadRelease}
        />
      )}

      {release.status === 'Processed' &&
        canManageReleaseArtifacts &&
        !release.nftMintAddress &&
        dapp?.nftMintAddress && (
          <div className="rounded-2xl bg-purple-500/[0.06] border border-purple-500/20 p-5 mb-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <span className="mt-1.5 inline-block size-1.5 rounded-full bg-purple-400 animate-pulse flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white">
                  Ready to publish on-chain
                </h3>
                <p className="text-xs text-neutral-400 mt-0.5">
                  Mint a Release NFT to publish this version on Solana.
                </p>
              </div>
            </div>
            <Button
              onClick={onMintClick}
              className="flex items-center gap-2 h-9 px-3.5 text-xs flex-shrink-0"
            >
              <TokenIcon className="size-3.5 fill-white" />
              Mint NFT
            </Button>
          </div>
        )}

      {release.nftMintAddress &&
        canManageReleaseArtifacts &&
        dapp?.nftMintAddress &&
        dapp?.walletAddress &&
        !release.isCollectionVerified && (
          <div className="rounded-2xl bg-amber-500/[0.06] border border-amber-500/20 p-5 mb-4 flex items-center justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <span className="mt-1.5 inline-block size-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white">
                  Collection verification required
                </h3>
                <p className="text-xs text-neutral-400 mt-0.5">
                  Verify this Release NFT as part of the App collection.
                </p>
              </div>
            </div>
            <VerifyCollectionButton
              dappId={dapp.id}
              nftMintAddress={release.nftMintAddress}
              collectionMintAddress={dapp.nftMintAddress}
              collectionAuthority={dapp.walletAddress}
              onSuccess={onVerificationSuccess}
              className="flex items-center gap-2 h-9 px-3.5 text-xs flex-shrink-0"
              variant="secondary"
              releaseId={release.id}
            />
          </div>
        )}
    </>
  );
}

interface ReleaseMintModalMountProps {
  release: ReleaseDto;
  dapp: Dapp | null;
  publisher: PublisherDto | null;
  showMintModal: boolean;
  onMintClose: () => void;
  onMintSuccess: (mintAddress: string, txSignature: string) => void;
}

function ReleaseMintModalMount({
  release,
  dapp,
  publisher,
  showMintModal,
  onMintClose,
  onMintSuccess,
}: ReleaseMintModalMountProps) {
  if (!showMintModal || !dapp || !publisher) {
    return null;
  }

  return (
    <ReleaseNftMintModal
      isOpen={showMintModal}
      onClose={onMintClose}
      onSuccess={onMintSuccess}
      releaseData={{
        id: release.id,
        releaseName: buildReleaseNftName({
          localizedName: release.localizedName,
          dappName: dapp.dappName,
          versionName: release.versionName,
        }),
        releaseVersion: release.versionName || 'Unknown',
        androidPackage: release.androidPackage || '',
        versionCode: release.versionCode || 0,
        permissions: release.permissions || [],
        minSdk: release.minSdkVersion || 0,
        targetSdk: release.targetSdkVersion,
        certificateFingerprint: release.certificateFingerprint || '',
        releaseFileUrl: release.releaseFileUrl,
        releaseFileName: release.releaseFileName,
        releaseFileSize: release.releaseFileSize,
        releaseFileHash: release.releaseFileHash || '',
        publisherAddress: dapp.walletAddress || '',
        appMintAddress: dapp.nftMintAddress || '',
        // Release metadata
        shortDescription: release.shortDescription ?? undefined,
        longDescription: release.longDescription ?? undefined,
        newInVersion: release.newInVersion ?? undefined,
        localizedName: release.localizedName ?? undefined,
        locales: release.locales ?? undefined,
      }}
      dappData={{
        id: dapp.id,
        dappName: dapp.dappName,
        subtitle: dapp.subtitle ?? undefined,
        description: dapp.description,
        dappIconUrl: dapp.dappIconUrl ?? undefined,
        dappIconThumbnailUrl: dapp.dappIconThumbnailUrl ?? undefined,
        bannerUrl: dapp.bannerUrl ?? undefined,
        featureGraphicUrl: dapp.featureGraphicUrl ?? undefined,
        editorsChoiceGraphicUrl: dapp.editorsChoiceGraphicUrl ?? undefined,
        dappPreviewUrls: dapp.dappPreviewUrls || [],
        // Publisher details
        publisherName: dapp.publisherName ?? undefined,
        publisherWebsite: dapp.publisherWebsite ?? undefined,
        publisherEmail: dapp.publisherEmail ?? undefined,
        contactEmail: dapp.contactEmail ?? undefined,
        supportEmail: dapp.supportEmail ?? undefined,
        // Legal URLs
        licenseUrl: (dapp.licenseUrl ?? undefined) as string | undefined,
        copyrightUrl: (dapp.copyrightUrl ?? undefined) as string | undefined,
        privacyPolicyUrl: (dapp.privacyPolicyUrl ?? undefined) as
          | string
          | undefined,
      }}
    />
  );
}
